import React, { useMemo } from 'react';
import { User } from 'redux/schemas/models/my-account';

import { css } from '@emotion/react';
import {
  gray3,
  gray6,
  warning,
  white,
} from 'styles/global_defaults/colors';
import { standardSpacing } from 'styles/global_defaults/scaffolding';

import NvCheckbox from 'shared/components/inputs/nv-checkbox';
import NvTooltip from 'shared/components/nv-tooltip';
import NvUserAvatar from 'components/nv-user-avatar';

export type SelectableUserItem = {
  id: number;
  user: {
    fullName: string;
    email?: string | null;
  };
};

type AdminSelectionListProps = {
  items: SelectableUserItem[];
  selectedItems: Record<string, boolean>;
  onToggleItem: (id: number, currentlySelected: boolean) => void;
  onToggleAll: (checked: boolean) => void;
  labels: {
    selectAll: string;
    unselectAll: string;
    counter: (selected: number, total: number) => string;
  };
  dataQa?: {
    selectAll?: string;
    itemCheckbox?: string;
  };
};

const styles = css`
  min-height: 0;
  display: flex;
  flex-direction: column;

  input {
    &:checked + label:before,
    &.indeterminate + label:before {
      color: ${white} !important;
      background-color: ${warning};
      border-color: ${warning} !important;
    }
  }

  label {
    margin: 0;
  }

  .select-all-row {
    .counter {
      .checkbox-container {
        width: ${standardSpacing}px;
        height: ${standardSpacing}px;
      }
    }
  }

  .admins-list {
    position: relative;
    overflow: hidden;
    overflow-y: auto;
    border-top: 1px solid ${gray3};

    .admin {
      border-bottom: 1px solid ${gray6};
    }
  }
`;

const AdminSelectionList = ({
  items,
  selectedItems,
  onToggleItem,
  onToggleAll,
  labels,
  dataQa = {},
}: AdminSelectionListProps) => {
  const totalCount = items.length;
  const selectedCount = useMemo(
    () => items.reduce((acc, item) => (selectedItems[String(item.id)] ? acc + 1 : acc), 0),
    [items, selectedItems],
  );
  const allSelected = totalCount > 0 && selectedCount === totalCount;
  const isIndeterminate = selectedCount > 0 && selectedCount < totalCount;

  const tooltipText = allSelected ? labels.unselectAll : labels.selectAll;
  const counterText = labels.counter(selectedCount, totalCount);

  return (
    <div css={styles} className='admin-selection-list'>
      <div className='select-all-row d-flex align-items-center pb-2 pl-4'>
        <div className='counter d-flex align-items-center'>
          <NvTooltip placement='top' text={tooltipText}>
            <div className='checkbox-container pt-2 mr-2'>
              <NvCheckbox
                label=''
                name='allAdmins'
                ariaLabel={tooltipText}
                indeterminate={isIndeterminate}
                checked={allSelected}
                onChange={(event) => onToggleAll(event.currentTarget.checked)}
                dataQa={dataQa.selectAll}
              />
            </div>
          </NvTooltip>
          <label htmlFor='allAdmins' className='text-regular font-weight-normal mb-0'>
            {counterText}
          </label>
        </div>
      </div>
      <div className='admins-list'>
        {items.map((item) => {
          const key = String(item.id);
          const selected = !!selectedItems[key];
          return (
            <div
              key={item.id}
              className={`admin px-4 py-3 d-flex align-items-center ${selected ? 'selected' : ''}`}
              data-qa={`clone-mentorship-admin-${item.id}`}
            >
              <NvCheckbox
                label=''
                name={`clone-admin-${item.id}`}
                ariaLabel={item.user?.email || item.user?.fullName || `admin-${item.id}`}
                checked={selected}
                onChange={() => onToggleItem(item.id, selected)}
                dataQa={dataQa.itemCheckbox}
              />
              <NvUserAvatar
                displayRoleBadge={false}
                size='md'
                user={item.user as Partial<User>}
              />
              <div className='info ml-2'>
                <div className='name font-weight-bold'>
                  {item.user.fullName}
                </div>
                {item.user.email && (
                  <div className='position text-small text-gray-2'>
                    {item.user.email}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AdminSelectionList;
